import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Github, Shield, Bug, Target, Code } from 'lucide-react';

const Projects: React.FC = () => {
  const projects = [
    {
      title: 'Vulnerability Scanner for Web Applications',
      description: 'Final Year Project - A comprehensive web application vulnerability scanner built with Python. Features include XSS detection, SQL injection testing, and automated security assessment.',
      tech: ['Python', 'Django', 'SQLite', 'Security Testing'],
      icon: Shield,
      link: '#',
      github: '#',
      featured: true
    },
    {
      title: 'XSS/SQLi Labs',
      description: 'Educational platform for learning Cross-Site Scripting and SQL Injection vulnerabilities. Includes interactive challenges and detailed walkthroughs.',
      tech: ['PHP', 'JavaScript', 'MySQL', 'Security Education'],
      icon: Bug,
      link: '#',
      github: '#'
    },
    {
      title: 'Bug Reports & Responsible Disclosure',
      description: 'Collection of responsibly disclosed vulnerabilities to various platforms and applications. Demonstrates ethical hacking practices and security research.',
      tech: ['Security Research', 'Bug Bounty', 'Responsible Disclosure'],
      icon: Target,
      link: '#',
      github: '#'
    },
    {
      title: 'HTB & TryHackMe Walkthroughs',
      description: 'Detailed walkthroughs and writeups for HackTheBox and TryHackMe challenges. Includes step-by-step solutions and learning resources.',
      tech: ['Penetration Testing', 'CTF', 'Writeups', 'Security Tools'],
      icon: Code,
      link: '#',
      github: '#'
    }
  ];

  return (
    <section id="projects" className="section-padding bg-cyber-dark">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <h2 className="text-3xl sm:text-4xl font-bold gradient-text mb-6">
            Projects
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Showcasing my work in cybersecurity, ethical hacking, and security research
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-10">
          {projects.map((project, index) => (
            <motion.div
              key={project.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`group relative bg-cyber-darker rounded-lg p-8 border border-cyber-blue/20 hover:border-cyber-blue/40 transition-all duration-300 ${
                project.featured ? 'lg:col-span-2' : ''
              }`}
            >
              {/* Featured Badge */}
              {project.featured && (
                <div className="absolute -top-3 left-6 bg-gradient-to-r from-cyber-blue to-cyber-purple text-white px-4 py-1 rounded-full text-sm font-medium">
                  Featured Project
                </div>
              )}

              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-gradient-to-br from-cyber-blue to-cyber-purple rounded-lg">
                      <project.icon size={24} className="text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white">{project.title}</h3>
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-300 leading-relaxed text-lg">
                  {project.description}
                </p>

                {/* Tech Stack */}
                <div className="flex flex-wrap gap-3">
                  {project.tech.map((tech) => (
                    <span
                      key={tech}
                      className="px-3 py-1 bg-cyber-dark/50 border border-cyber-blue/20 rounded-full text-xs text-cyber-blue font-mono"
                    >
                      {tech}
                    </span>
                  ))}
                </div>

                {/* Links */}
                <div className="flex gap-4 pt-4">
                  <a
                    href={project.link}
                    className="flex items-center gap-2 text-cyber-blue hover:text-cyber-purple transition-colors"
                  >
                    <ExternalLink size={16} />
                    <span className="text-sm">View Project</span>
                  </a>
                  <a
                    href={project.github}
                    className="flex items-center gap-2 text-gray-400 hover:text-cyber-blue transition-colors"
                  >
                    <Github size={16} />
                    <span className="text-sm">View Code</span>
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects; 